import { useState, forwardRef, useImperativeHandle } from "react";
import { useApp } from "../context/AppContext";
import {
  Search, Users, Euro, MapPin, Sparkles, Bot, Calendar as CalIcon,
  Baby, Clock, CloudRain, Wand2, SlidersHorizontal,
} from "lucide-react";

const MOOD_TAGS_ES = ["Naturaleza", "Aventura", "Tapas", "Escape room", "Relax", "Cultural", "Fiesta"];
const MOOD_TAGS_EN = ["Nature", "Adventure", "Tapas", "Escape room", "Relax", "Cultural", "Party"];

const PlanForm = forwardRef(function PlanForm({ onSubmit, onSurprise, loading }, ref) {
  const { t, lang } = useApp();
  const [people, setPeople] = useState(4);
  const [budget, setBudget] = useState(30);
  const [mood, setMood] = useState("");
  const [distance, setDistance] = useState(60);
  const [location, setLocation] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [aiModel, setAiModel] = useState("claude");
  const [date, setDate] = useState("");
  const [withKids, setWithKids] = useState(false);
  const [duration, setDuration] = useState("full");
  const [weatherAware, setWeatherAware] = useState(true);
  const [ageRange, setAgeRange] = useState("");
  const [showMore, setShowMore] = useState(false);

  // Expose applyParsed to parent (called by NaturalLanguageInput)
  useImperativeHandle(ref, () => ({
    applyParsed(data) {
      if (!data) return;
      if (data.people != null) setPeople(String(data.people));
      if (data.budget_per_person != null) setBudget(String(data.budget_per_person));
      if (data.mood) setMood(data.mood);
      if (data.max_distance_km != null) setDistance(String(data.max_distance_km));
      if (data.location) setLocation(data.location);
      if (data.date) setDate(data.date);
      if (data.with_kids === true || data.with_kids === false) setWithKids(!!data.with_kids);
      if (data.age_range) { setAgeRange(data.age_range); setShowMore(true); }
      if (data.duration === "half" || data.duration === "full") setDuration(data.duration);
      if (data.age_range || data.with_kids != null) setShowMore(true);
    },
  }));

  const tags = lang === "es" ? MOOD_TAGS_ES : MOOD_TAGS_EN;

  const toggleTag = (tag) => {
    setSelectedTags((prev) => prev.includes(tag) ? prev.filter(x => x !== tag) : [...prev, tag]);
  };

  const build = (overrides = {}) => {
    const moodStr = [mood, ...selectedTags].filter(Boolean).join(", ");
    return {
      people: Number(people),
      budget_per_person: Number(budget),
      mood: moodStr,
      max_distance_km: Number(distance),
      location: location.trim(),
      language: lang,
      ai_model: aiModel,
      date: date || null,
      with_kids: withKids,
      age_range: ageRange || null,
      duration,
      weather_aware: weatherAware,
      surprise: false,
      avoid_titles: [],
      ...overrides,
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(build());
  };

  const handleSurprise = () => {
    if (!location.trim()) return;
    onSurprise(build({ surprise: true }));
  };

  const canSubmit = location.trim().length > 1 && !loading;

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="plan-form"
      className="bg-white rounded-3xl border border-[#E7E0D5] shadow-[0_4px_24px_rgba(28,25,23,0.06)] p-6 sm:p-8"
    >
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label={t.people} icon={<Users className="w-4 h-4" />}>
          <input data-testid="input-people" type="number" min={1} max={30} value={people} onChange={(e) => setPeople(e.target.value)} className="input-base" />
        </Field>

        <Field label={t.budgetPerPerson} icon={<Euro className="w-4 h-4" />}>
          <input data-testid="input-budget" type="number" min={0} step={5} value={budget} onChange={(e) => setBudget(e.target.value)} className="input-base" />
        </Field>

        <Field label={t.location} icon={<MapPin className="w-4 h-4" />} className="sm:col-span-2">
          <input data-testid="input-location" type="text" required value={location} onChange={(e) => setLocation(e.target.value)} placeholder={t.locationPlaceholder} className="input-base" />
        </Field>

        <Field label={t.date} icon={<CalIcon className="w-4 h-4" />}>
          <input data-testid="input-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-base" />
        </Field>

        <Field label={t.duration} icon={<Clock className="w-4 h-4" />}>
          <div className="inline-flex rounded-full bg-[#F2EDE4] p-1 border border-[#E7E0D5] w-full">
            <button type="button" data-testid="toggle-duration-half" onClick={() => setDuration("half")}
              className={`flex-1 text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${duration === "half" ? "bg-white text-stone-900 shadow-sm" : "text-stone-600"}`}>
              {t.halfDay}
            </button>
            <button type="button" data-testid="toggle-duration-full" onClick={() => setDuration("full")}
              className={`flex-1 text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${duration === "full" ? "bg-white text-stone-900 shadow-sm" : "text-stone-600"}`}>
              {t.fullDay}
            </button>
          </div>
        </Field>

        <Field label={t.mood} icon={<Sparkles className="w-4 h-4" />} className="sm:col-span-2">
          <input data-testid="input-mood" type="text" value={mood} onChange={(e) => setMood(e.target.value)} placeholder={t.moodPlaceholder} className="input-base" />
          <div className="flex flex-wrap gap-2 mt-3">
            {tags.map((tag) => (
              <button type="button" key={tag} data-testid={`chip-${tag.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => toggleTag(tag)}
                className={`px-3 py-1 text-xs rounded-full border transition-all ${
                  selectedTags.includes(tag) ? "bg-[#2C5E3B] text-white border-[#2C5E3B]" : "bg-white text-stone-700 border-[#E7E0D5] hover:border-[#2C5E3B]"
                }`}>
                {tag}
              </button>
            ))}
          </div>
        </Field>

        <Field label={`${t.maxDistance} — ${t.upTo} ${distance} ${t.km}`} className="sm:col-span-2">
          <input data-testid="input-distance" type="range" min={5} max={200} step={5} value={distance} onChange={(e) => setDistance(e.target.value)} className="w-full accent-[#D95338]" />
        </Field>

        {showMore && (
          <>
            <Field label={t.ageRange} className="sm:col-span-1">
              <input data-testid="input-age-range" type="text" value={ageRange} onChange={(e) => setAgeRange(e.target.value)} placeholder={t.ageRangePh} className="input-base" />
            </Field>
            <div className="sm:col-span-1 flex flex-col gap-3 justify-end">
              <ToggleRow icon={<Baby className="w-4 h-4" />} label={t.withKids} checked={withKids} onChange={setWithKids} testid="toggle-with-kids" />
              <ToggleRow icon={<CloudRain className="w-4 h-4" />} label={t.weatherAware} checked={weatherAware} onChange={setWeatherAware} testid="toggle-weather" />
            </div>
          </>
        )}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button type="button" onClick={() => setShowMore((v) => !v)} data-testid="button-more-filters"
          className="text-xs font-mono uppercase tracking-wider text-stone-500 hover:text-stone-900 inline-flex items-center gap-1.5">
          <SlidersHorizontal className="w-3.5 h-3.5" />
          {showMore ? t.hideFilters : t.moreFilters}
        </button>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <button type="submit" data-testid="button-search-plans" disabled={!canSubmit}
          className="flex-1 sm:flex-none px-8 py-3.5 rounded-full bg-[#D95338] hover:bg-[#C24127] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold flex items-center justify-center gap-2 active:scale-95 transition-all shadow-lg shadow-[#D95338]/20">
          <Search className="w-4 h-4" />
          {loading ? t.searching : t.searchPlans}
        </button>
        <button type="button" onClick={handleSurprise} disabled={!canSubmit} data-testid="button-surprise"
          className="px-6 py-3.5 rounded-full bg-[#2C5E3B] hover:bg-[#21472C] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold inline-flex items-center gap-2 active:scale-95 transition-all">
          <Wand2 className="w-4 h-4" />
          {t.surpriseMe}
        </button>
      </div>

      <div className="mt-5 pt-4 border-t border-[#E7E0D5]/70 flex items-center justify-between flex-wrap gap-3">
        <span className="text-xs uppercase tracking-wider font-mono font-medium text-stone-500 flex items-center gap-1.5">
          <Bot className="w-3.5 h-3.5" />{t.aiEngine}
        </span>
        <div className="inline-flex rounded-full bg-[#F2EDE4] p-1 border border-[#E7E0D5]">
          <button type="button" data-testid="toggle-model-claude" onClick={() => setAiModel("claude")}
            className={`text-xs font-semibold px-3.5 py-1.5 rounded-full transition-all ${aiModel === "claude" ? "bg-white text-stone-900 shadow-sm" : "text-stone-600"}`}>
            Claude Sonnet 5
          </button>
          <button type="button" data-testid="toggle-model-gpt" onClick={() => setAiModel("gpt")}
            className={`text-xs font-semibold px-3.5 py-1.5 rounded-full transition-all ${aiModel === "gpt" ? "bg-white text-stone-900 shadow-sm" : "text-stone-600"}`}>
            ChatGPT 5.6
          </button>
        </div>
      </div>
    </form>
  );
});

export default PlanForm;

function Field({ label, icon, children, className = "" }) {
  return (
    <label className={`flex flex-col gap-2 ${className}`}>
      <span className="text-xs uppercase tracking-wider font-mono font-medium text-stone-500 flex items-center gap-1.5">
        {icon}{label}
      </span>
      {children}
    </label>
  );
}

function ToggleRow({ icon, label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-2xl bg-[#FAF7F2] border border-[#E7E0D5] cursor-pointer">
      <span className="text-sm text-stone-700 flex items-center gap-2">{icon}{label}</span>
      <button type="button" data-testid={testid} onClick={() => onChange(!checked)}
        className={`w-11 h-6 rounded-full transition-colors relative ${checked ? "bg-[#2C5E3B]" : "bg-stone-300"}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${checked ? "left-5" : "left-0.5"}`} />
      </button>
    </label>
  );
}
